import PropTypes from "prop-types"
import { useEffect, useMemo, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { useDispatch } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"

import {
  clearCollection,
  setCollection,
  setLoading,
} from "areas/collection/store/actions"
import { submit_ga_tracker } from "helpers/analytics-helpers"
import { jewlr_t } from "helpers/application"
import { getHreflangs } from "helpers/meta"

import useCollection from "./use-collection"

export default function fetchCollection(WrappedComponent) {
  const FetchCollection = ({
    collectionData,
    landingData,
    ssrModel,
    ...props
  }) => {
    const { url_path: urlPath = "homepage" } = useParams()
    const currentUrlPath = useRef(urlPath)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const ssrData =
      typeof window === "undefined"
        ? ssrModel === "collection"
          ? collectionData
          : landingData
        : undefined

    const queryData = useCollection(ssrData)
    const {
      data,
      fetchPreviousPage,
      hasPreviousPage,
      isFetchingPreviousPage,
      isLoading,
      isPreviousData,
    } = queryData
    const { model } = data || {}

    const isSearch = urlPath === "search"

    useEffect(() => {
      dispatch(clearCollection())

      return () => {
        window.cache_free_ssr_data.current_page = null
      }
    }, [urlPath])

    useEffect(() => {
      // When SSR loading a page > 1, we only have the data from the current page.
      // Here, we gradually fetch each previous page back to page 1.
      if (hasPreviousPage && !isFetchingPreviousPage) {
        fetchPreviousPage()
      }
    }, [fetchPreviousPage, isFetchingPreviousPage, hasPreviousPage])

    useEffect(() => {
      if (isLoading || isPreviousData) {
        dispatch(
          setLoading(
            !!model && currentUrlPath.current === urlPath ? "partial" : "full"
          )
        )
      } else {
        dispatch(setLoading(""))
      }
      currentUrlPath.current = urlPath
    }, [isLoading, isPreviousData, urlPath, model])

    useEffect(() => {
      if (!data) return

      if (data.redirect_url) {
        if (data.custom_page_view) {
          submit_ga_tracker(data.custom_page_view)
        }

        navigate(data.redirect_url, { replace: true })
        return
      }

      if (model === "collection") {
        dispatch(setCollection(data))
      }
    }, [data])

    useEffect(() => {
      if (!data) return

      if (window.cache_free_ssr_data?.current_page) {
        navigate(`/${window.cache_free_ssr_data.current_page}`, {
          replace: true,
        })

        window.cache_free_ssr_data.current_page = null
      } else if (data.pagination?.current_page) {
        navigate(`/${data.pagination.current_page}`, { replace: true })
      }
    }, [data?.pagination?.current_page])

    const seoData = useMemo(() => {
      if (!data) return {}

      let pageUrl = ""
      let nextPageUrl = ""
      let prevPageUrl = ""
      let productItemList = []
      let schemaName = ""
      let schemaDescription = ""
      let seoTitle = ""
      let seoDescription = ""
      let breadcrumbList = []
      let faqs = []
      let noindex = isSearch

      if (model === "collection") {
        pageUrl = data.pagination.current_page
        productItemList = data.product_result.map((product, index) => ({
          "@type": "ListItem",
          position: index,
          url: `https://${sl.config.site_url}/products/${product.auto_created_look_style_code}`,
        }))
        nextPageUrl = data.pagination.next_page
        prevPageUrl = data.pagination.prev_page
        seoTitle = isSearch
          ? `Search ${jewlr_t("terms.Personalized")} ${jewlr_t("terms.Jewelry")} & Gifts`
          : data.seo_title
        seoDescription = isSearch
          ? `Use Jewlr’s search tool to explore ${jewlr_t("terms.personalized")} rings, necklaces, bracelets, and gifts. ${jewlr_t("terms.Customize")} with birthstones, engravings, and metals.`
          : data.seo_description
        schemaName = data.schema_name || seoTitle
        schemaDescription = data.schema_description || seoDescription
        noindex = data.noindex

        breadcrumbList = data.breadcrumb.map((item, i) => ({
          "@type": "ListItem",
          item: {
            "@id": `https://${sl.config.site_url}${item.url || `/${pageUrl}`}`,
            name: item.seo_title,
          },
          position: i + 1,
        }))

        faqs = (data.faqs || []).map((faq) => ({
          "@type": "Question",
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
          name: faq.question,
        }))
      } else if (model === "landing" && data.url_path !== "homepage") {
        pageUrl = data.url_path
        productItemList = data.products.map((product, index) => ({
          "@type": "ListItem",
          position: index,
          url: `https://${sl.config.site_url}/products/${product.style_code}`,
        }))
        seoTitle = data.seo_title
        seoDescription = data.seo_description

        breadcrumbList = [
          {
            "@type": "ListItem",
            item: {
              "@id": `https://${sl.config.site_url}`,
              name: "Homepage",
            },
            position: 1,
          },
          {
            "@type": "ListItem",
            item: {
              "@id": `https://${sl.config.site_url}/${pageUrl}`,
              name: seoTitle,
            },
            position: 2,
          },
        ]
      }

      return {
        breadcrumbList,
        faqs,
        nextPageUrl,
        noindex,
        pageUrl,
        prevPageUrl,
        productItemList,
        schemaDescription,
        schemaName,
        seoDescription,
        seoTitle,
      }
    }, [data, model, isSearch])

    const {
      breadcrumbList = [],
      faqs = [],
      nextPageUrl,
      noindex = isSearch,
      pageUrl,
      prevPageUrl,
      productItemList = [],
      schemaDescription,
      schemaName,
      seoDescription,
      seoTitle,
    } = seoData

    return (
      <>
        {/* eslint-disable sort-keys */}
        <Helmet>
          {noindex && <meta content="noindex" name="robots" />}
          {data?.canonical_collection && (
            <link
              href={`https://${sl.config.site_url}/${data.canonical_collection}`}
              rel="canonical"
            />
          )}
          {prevPageUrl && (
            <link
              href={`https://${sl.config.site_url}/${prevPageUrl}`}
              rel="prev"
            />
          )}
          {nextPageUrl && (
            <link
              href={`https://${sl.config.site_url}/${nextPageUrl}`}
              rel="next"
            />
          )}
          {seoTitle && (
            <>
              <title>{seoTitle}</title>
              <meta content={seoTitle} property="og:title" />
            </>
          )}
          {seoDescription && (
            <meta content={seoDescription} name="description" />
          )}
          {seoDescription && (
            <meta content={seoDescription} property="og:description" />
          )}
          {pageUrl && (
            <meta
              content={`https://${sl.config.site_url}/${pageUrl}`}
              property="og:url"
            />
          )}
          {pageUrl &&
            getHreflangs(`/${pageUrl}`).map(({ href, hreflang }) => (
              <link
                href={href}
                hrefLang={hreflang}
                key={hreflang}
                rel="alternate"
              />
            ))}
          {model === "collection" && (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                ...(schemaName && { name: schemaName }),
                ...(pageUrl && {
                  url: `https://${sl.config.site_url}/${pageUrl}`,
                }),
                ...(schemaDescription && { description: schemaDescription }),
                publisher: {
                  "@type": "Organization",
                  name: sl.config.site_title,
                  url: `https://${sl.config.site_url}`,
                  logo: {
                    "@type": "ImageObject",
                    url: "https://assets.jewlr.com/images/logos/jewlr-logo.svg",
                  },
                },
              })}
            </script>
          )}
          {(model == "collection" || model == "landing") &&
            productItemList.length > 0 && (
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "ItemList",
                  url: `https://${sl.config.site_url}/${pageUrl}`,
                  numberOfItems: productItemList.length,
                  itemListElement: productItemList,
                })}
              </script>
            )}
          {breadcrumbList.length > 0 && (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbList,
              })}
            </script>
          )}
          {faqs.length > 0 && (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs,
              })}
            </script>
          )}
        </Helmet>
        {/* eslint-enable sort-keys */}
        <WrappedComponent model={model} {...props} />
      </>
    )
  }

  FetchCollection.propTypes = {
    collectionData: PropTypes.object,
    landingData: PropTypes.object,
    ssrModel: PropTypes.string,
  }

  return FetchCollection
}
